import { defineStore } from 'pinia'
import { getAllOrders } from '@/api/orders/getAllOrders'
import { createOrder } from '@/api/orders/createOrder'
import { getOrderById } from '@/api/orders/getOrderById'

interface OrderStore {
  orders: any[]
  products: any[]
  loading: boolean
  totalItems: number
}

export const useOrdersStore = defineStore('orders', {
  state: (): OrderStore => ({
    orders: [],
    products: [],
    loading: false,
    totalItems: 0
  }),

  getters: {
    tables: (state) => {
      const tableMap = new Map<string, any[]>()

      state.orders.forEach((order) => {
        const tablenumMeta = order.meta_data.find((meta: any) => meta.key === 'tablenum')
        const tablenum = tablenumMeta ? tablenumMeta.value : 'unknown'

        if (!tableMap.has(tablenum)) {
          tableMap.set(tablenum, [])
        }
        tableMap.get(tablenum)?.push(order)
      })

      return Array.from(tableMap, ([tablenum, orders]) => ({ tablenum, orders }))
    }
  },

  actions: {
    async getOrders() {
      this.loading = true
      const { data, success } = await getAllOrders()
      if (success) {
        this.orders = data.orders
        this.totalItems = data.total_items
        this.loading = false
      }
      return {
        success
      }
    },

    async createTable(payload: any) {
      const newTable = await createOrder(payload)
      // Check if order was already added by Pusher to avoid duplicates
      // Use == for loose comparison to handle string/number type differences
      if (newTable.data && !this.orders.some((order) => order.id == newTable.data.id)) {
        this.orders.unshift(newTable.data)
      }
    },

    async fetchAndUpsertOrder(
      orderId: number
    ): Promise<{ success: boolean; isNew: boolean; order: any | null }> {
      const { data, success } = await getOrderById(orderId)
      if (success && data) {
        // Check if order exists AFTER fetching (in case it was added during the delay)
        // Use == for loose comparison to handle string/number type differences
        const existingIndex = this.orders.findIndex((order) => order.id == orderId)
        const isNew = existingIndex === -1

        if (!isNew) {
          // Update existing order with fresh data
          this.orders[existingIndex] = data
        } else {
          // Add new order at the beginning
          this.orders.unshift(data)
        }
        return { success: true, isNew, order: data }
      }
      return { success: false, isNew: false, order: null }
    }
  }
})
